import React from 'react';
import { trackEvent, BOOKING_SCREENING_URL, PHONE_NUMBER, PHONE_TEL, EMAIL_ADDRESS } from '../lib/tracking';
import ContactForm from '../components/ContactForm';
import TrustBar from '../components/TrustBar';

export default function Contact() {
  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
              Tag kontakt
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
              Kontakt Kim Palasz
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#6c7480] leading-relaxed">
              Tag det første skridt mod forandring. Du kan booke en gratis afklaringssamtale direkte i kalenderen eller sende en besked via formularen.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content: Info & Form */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left: Contact Details & Direct Booking */}
            <div className="lg:col-span-5 space-y-8">
              {/* Direct Booking Highlight */}
              <div className="bg-[#5A232E] text-white p-8 rounded-3xl shadow-lg space-y-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  Hurtigste vej til en samtale
                </span>
                <h2 className="font-display text-2xl text-white">
                  Book gratis afklaringssamtale (20 min.)
                </h2>
                <p className="text-xs sm:text-sm text-white/80 leading-relaxed">
                  Vælg et tidspunkt direkte i online-kalenderen, der passer dig. Samtalen foregår over telefon eller online og er 100% uforpligtende.
                </p>
                <a
                  href={BOOKING_SCREENING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: 'contact_page_highlight' })}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
                >
                  <span>Åbn online booking</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                </a>
              </div>

              {/* Practice details */}
              <div className="bg-white p-8 rounded-3xl border border-[#D9DDE2] shadow-xs space-y-5 text-sm text-[#11161d]">
                <h3 className="font-display text-xl text-[#5A232E]">
                  Praksisoplysninger
                </h3>

                <div className="space-y-4 pt-1">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#ECEEF1] text-[#5A232E] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot text-[#5A232E]" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-[#5A232E]">Klinikadresse:</strong>
                      <span>Psykoterapeut Kim Palasz</span><br />
                      <span>Marengovej 2</span><br />
                      <span>2300 København S</span>
                      <div className="mt-1">
                        <a
                          href="https://www.google.com/maps/search/?api=1&query=Kim+Palasz+Psykoterapeut+MPF+Marengovej+2+2300+København"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#5A232E] font-medium hover:underline flex items-center gap-1"
                        >
                          <span>Find vej på Google Maps</span>
                          <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#ECEEF1] text-[#5A232E] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone text-[#5A232E]" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-[#5A232E] text-xs uppercase tracking-wider">Telefon:</strong>
                      <a
                        href={PHONE_TEL}
                        onClick={() => trackEvent('phone_click', { location: 'contact_page' })}
                        className="text-sm font-semibold text-[#5A232E] hover:text-[#5A232E] no-break whitespace-nowrap"
                      >
                        {PHONE_NUMBER}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#ECEEF1] text-[#5A232E] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope text-[#5A232E]" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-[#5A232E] text-xs uppercase tracking-wider">E-mail:</strong>
                      <a
                        href={`mailto:${EMAIL_ADDRESS}`}
                        onClick={() => trackEvent('email_click', { location: 'contact_page' })}
                        className="text-sm font-semibold text-[#5A232E] hover:text-[#5A232E] no-break whitespace-nowrap"
                      >
                        {EMAIL_ADDRESS}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#ECEEF1] text-[#5A232E] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-building text-[#5A232E]" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-[#5A232E] text-xs uppercase tracking-wider">Virksomhed:</strong>
                      <span>CVR-nr: 36149914</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl p-8 sm:p-12 border border-[#D9DDE2] shadow-xs">
                <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-2">
                  Send en besked
                </span>
                <h2 className="font-display text-2xl sm:text-3xl text-[#5A232E] mb-3">
                  Skriv til mig
                </h2>
                <p className="text-xs sm:text-sm text-[#6c7480] mb-8 leading-relaxed">
                  Har du spørgsmål til et forløb, supervision eller priser? Udfyld felterne nedenfor, så svarer jeg snarest muligt.
                </p>

                <ContactForm subjectDefault="Henvendelse via palasz.dk" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />
    </div>
  );
}
